<script setup lang="ts">
useSeoMeta({
  title: 'QikSolve — Practical Quality Systems on Microsoft 365',
  description:
    'QikSolve helps regulated teams build practical quality systems and controlled digital operations on Microsoft 365.',
});

const stats = [
  { value: '2014', label: 'Founded in Melbourne' },
  { value: '100%', label: 'Built on your Microsoft 365 platform' },
  { value: 'GMP / GxP', label: 'Quality systems for regulated teams' },
];

const services = [
  {
    icon: 'i-heroicons-arrow-path-rounded-square',
    title: 'Paper-to-Digital Quality Transformation',
    description: 'Move fragmented paper and office-based records towards controlled digital quality processes.',
    to: '/services',
  },
  {
    icon: 'i-heroicons-wrench-screwdriver',
    title: 'Software Implementation, Maintenance and Management',
    description: 'Implement and govern controlled software without coordinating disconnected platform and assurance work.',
    to: '/services',
  },
  {
    icon: 'i-heroicons-map',
    title: 'Quality Process Mapping, Development and Implementation',
    description: 'Turn current quality and business practices into workable, documented, controlled processes.',
    to: '/services',
  },
  {
    icon: 'i-heroicons-cog-6-tooth',
    title: 'SharePoint Governance',
    description: 'Shape Microsoft 365 information and workflows around your quality, records, and evidence needs.',
    to: '/product/sharepoint-governance',
  },
  {
    icon: 'i-heroicons-shield-check',
    title: 'Regulatory Compliance, Validation and CSV Support',
    description: 'Build a clearer compliance and validation approach for Microsoft 365 and regulated operations.',
    to: '/product/regulatory-compliance',
  },
  {
    icon: 'i-heroicons-chart-bar-square',
    title: 'Quality Dashboards',
    description: 'Turn quality and operational information into management insight for better decisions.',
    to: '/product/quality-dashboards',
  },
  {
    icon: 'i-heroicons-clipboard-document-check',
    title: 'ISO 9001',
    description: 'Apply ISO 9001 as a practical context for everyday quality decisions, records, and improvement.',
    to: '/product/iso-9001',
  },
  {
    icon: 'i-heroicons-cpu-chip',
    title: 'Agentic AI Governance',
    description: 'Introduce risk-tiered governance for generative AI and agentic workflows.',
    to: '/product/agentic-ai-governance',
  },
  {
    icon: 'i-heroicons-academic-cap',
    title: 'Training and Enablement',
    description: 'Build role-based capability across CSV, SharePoint governance, and AI governance.',
    to: '/product/training',
  },
];

const problems = [
  {
    title: 'We need practical GxP quality support',
    description:
      'Move from paper and fragmented records towards practical, controlled digital quality processes that support GxP expectations.',
    to: '/product/regulatory-compliance',
  },
  {
    title: 'We need to validate an Excel quality register',
    description:
      'Assess the register\'s intended use, risk, controls, and evidence before deciding whether to retain, remediate, or migrate it to SharePoint.',
    to: '/explore-by-need/validate-excel-quality-register',
  },
  {
    title: 'We need an effective ISO 9001 quality system',
    description:
      'Build a practical quality system around continual improvement, customer satisfaction, clear ownership, and everyday quality decisions.',
    to: '/product/iso-9001',
  },
  {
    title: 'We need validation support and training',
    description: 'Build the validation evidence and team capability needed to implement and sustain controlled quality processes.',
    to: '/product/training',
  },
  {
    title: 'We need to govern AI in a regulated environment',
    description: 'Apply risk-tiered governance, human oversight, evidence, and accountability to AI-assisted quality workflows.',
    to: '/product/agentic-ai-governance',
  },
  {
    title: 'We need better quality reporting with Power BI',
    description: 'Turn quality data into useful management insight with Power BI reporting designed around your decisions.',
    to: '/product/quality-dashboards',
  },
];

const industries = [
  {
    icon: 'i-heroicons-beaker',
    title: 'Pharmaceutical Manufacturers',
    description:
      'GMP Compliance is an essential part of daily life in the pharmaceutical industry. An integrated electronic documentation system solves your paper challenges.',
  },
  {
    icon: 'i-heroicons-cake',
    title: 'Food and Beverage Manufacturers',
    description:
      'Implement the right systems, policies, procedures, and controls to build an efficient and reliable Quality Management System.',
  },
  {
    icon: 'i-heroicons-heart',
    title: 'Clinical Research Organisations',
    description:
      'A SharePoint Quality Management System is an ideal solution for CROs who need to deliver a high-quality service and care.',
  },
  {
    icon: 'i-heroicons-cpu-chip',
    title: 'Medical Device Manufacturers',
    description:
      'Compliance with the ISO 13485 standard is essential to consistently meet customer and regulatory requirements.',
  },
];

const products = [
  {
    name: 'INQ eQMS',
    family: 'INQ',
    description:
      'The core INQ quality management system pathway for regulated teams working within the Microsoft 365 environment they already use.',
    to: 'https://inq.qiksolve.com',
    icon: 'i-heroicons-clipboard-document-check',
  },
  {
    name: 'INQ TMS',
    family: 'INQ',
    description:
      'Support training management, competency, and evidence across regulated quality operations.',
    to: 'https://inq.qiksolve.com',
    icon: 'i-heroicons-academic-cap',
  },
  {
    name: 'INQ eDMS & eRMS',
    family: 'INQ',
    description:
      'Support controlled document and records management with traceable quality information for regulated teams.',
    to: 'https://inq.qiksolve.com',
    icon: 'i-heroicons-document-text',
  },
  {
    name: 'QxReview',
    family: 'QxAIOS',
    description:
      'Agent-driven review of scanned paper records, including batch records, to surface potential ALCOA+ and consistency issues before human verification.',
    to: 'https://qx.qiksolve.com',
    icon: 'i-heroicons-clipboard-document-check',
  },
  {
    name: 'QxVerify',
    family: 'QxAIOS',
    description:
      'Turn agent-reviewed paper-record data into a true digital copy for Quality review and sign-off, so approved records can be searched, traced, and reused for downstream quality insight.',
    to: 'https://qx.qiksolve.com',
    icon: 'i-heroicons-shield-check',
  },
  {
    name: 'QxInsight',
    family: 'QxAIOS',
    description:
      'Turn verified digital-record data into usable information for quality and operational insight.',
    to: 'https://qx.qiksolve.com',
    icon: 'i-heroicons-chart-bar-square',
  },
];
</script>

<template>
  <div>
    <section
      id="hero"
      class="relative scroll-mt-24 overflow-hidden bg-gradient-to-br from-brand-500 via-brand-700 to-brand-900 text-white"
    >
      <div class="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_20%_20%,white,transparent_35%),radial-gradient(circle_at_80%_60%,white,transparent_30%)]" />
      <div class="relative max-w-5xl mx-auto px-4 py-24 text-center">
        <span class="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium ring-1 ring-white/20 mb-6">
          <span
            class="size-2 rounded-full bg-white/80"
            aria-hidden="true"
          />
          Practical quality systems on Microsoft 365
        </span>
        <h1 class="text-4xl sm:text-5xl font-heading font-extrabold mb-5 leading-tight tracking-tight">
          Can the Microsoft 365 platform you already use meet your quality and compliance goals?
        </h1>
        <p class="text-lg text-brand-100 max-w-3xl mx-auto">
          Navigating complex regulatory and compliance needs alongside competing operational pressures is difficult
          for any regulated business. Explore the challenges QikSolve has helped regulated teams work through, from
          making more of Microsoft 365 to preparing for validation and improving quality workflows.
        </p>
        <div class="mt-8 flex flex-wrap items-center justify-center gap-4">
          <NuxtLink
            to="/explore-by-need"
            class="inline-flex min-h-12 items-center rounded-lg bg-white px-5 py-3 font-semibold text-brand-700 transition-colors hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            Explore by Need
          </NuxtLink>
          <NuxtLink
            to="/contact"
            class="inline-flex min-h-12 items-center rounded-lg border border-white/60 px-5 py-3 font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            Book a discovery call
          </NuxtLink>
        </div>

        <dl class="mt-16 grid grid-cols-3 gap-6 border-t border-white/20 pt-8">
          <div
            v-for="stat in stats"
            :key="stat.label"
          >
            <dt class="text-sm text-brand-100 mt-1">
              {{ stat.label }}
            </dt>
            <dd class="text-2xl sm:text-3xl font-heading font-bold">
              {{ stat.value }}
            </dd>
          </div>
        </dl>
      </div>
    </section>

    <section class="border-y border-brand-100 bg-brand-50/70 py-16">
      <div class="mx-auto max-w-5xl px-4 text-center">
        <p class="mb-3 text-sm font-semibold uppercase tracking-wider text-brand-700">
          The QikSolve approach
        </p>
        <h2 class="mx-auto max-w-3xl text-3xl font-heading font-bold text-gray-900 sm:text-4xl">
          Start with the platform you and your team already know and use every day.
        </h2>
        <p class="mx-auto mt-5 max-w-2xl text-lg leading-8 text-gray-700">
          QikSolve helps regulated organisations start with the Microsoft 365 capabilities they already know and
          use, adding only what is needed for their quality and regulatory requirements. We translate regulatory
          expectations and audit considerations into practical systems that connect people, processes, information,
          technology, and evidence.
        </p>
        <div class="mt-8 flex flex-col items-center justify-center gap-3 text-sm font-semibold text-brand-800 sm:flex-row sm:gap-4">
          <span>Understand the need</span>
          <UIcon
            name="i-heroicons-arrow-down-20-solid"
            class="size-4 sm:rotate-[-90deg]"
            aria-hidden="true"
          />
          <span>Use what you have</span>
          <UIcon
            name="i-heroicons-arrow-down-20-solid"
            class="size-4 sm:rotate-[-90deg]"
            aria-hidden="true"
          />
          <span>Build what is needed</span>
        </div>
      </div>
    </section>

    <section
      id="problems"
      class="max-w-6xl mx-auto scroll-mt-24 px-4 py-20"
    >
      <div class="max-w-3xl mx-auto mb-10 text-center">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-brand-600 mb-2">
          Explore by Need
        </h2>
        <p class="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-4">
          What quality challenges are you facing?
        </p>
        <p class="text-lg text-gray-600">
          Navigating complex regulatory and compliance needs is difficult for any regulated business. Explore the
          challenges QikSolve has helped regulated teams work through, from making more of Microsoft 365 to
          preparing for validation and improving quality workflows.
        </p>
        <NuxtLink
          to="/explore-by-need"
          class="mt-5 inline-flex items-center gap-2 font-semibold text-brand-700 hover:text-brand-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          Explore all need-led pathways
          <UIcon
            name="i-heroicons-arrow-right-20-solid"
            class="size-4"
            aria-hidden="true"
          />
        </NuxtLink>
      </div>
      <div class="grid gap-5 md:grid-cols-2">
        <NuxtLink
          v-for="problem in problems"
          :key="problem.title"
          :to="problem.to"
          class="group rounded-2xl border border-gray-200 bg-white p-6 shadow-[var(--shadow-card)] transition-all duration-200 hover:-translate-y-1 hover:border-brand-200 hover:shadow-[var(--shadow-card-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          <div class="flex items-start justify-between gap-4">
            <h3 class="text-xl font-heading font-bold text-gray-900 group-hover:text-brand-700">
              {{ problem.title }}
            </h3>
            <UIcon
              name="i-heroicons-arrow-up-right-20-solid"
              class="size-5 shrink-0 text-brand-600 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </div>
          <p class="mt-3 text-gray-600">
            {{ problem.description }}
          </p>
        </NuxtLink>
      </div>
    </section>

    <section
      id="products"
      class="max-w-6xl mx-auto scroll-mt-24 px-4 pb-20"
    >
      <div class="text-center mb-10">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-brand-600 mb-2">
          Products
        </h2>
        <p class="text-2xl sm:text-3xl font-heading font-bold text-gray-900">
          Choose the product pathway that fits your question
        </p>
        <p class="mt-3 mx-auto max-w-2xl text-gray-600">
          QikSolve provides the context; INQ eQMS is the core quality-management pathway, supported by related INQ
          products and Qx pathways within the Microsoft 365 ecosystem.
        </p>
      </div>
      <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-20">
        <a
          v-for="product in products"
          :key="product.name"
          :href="product.to"
          target="_blank"
          rel="noopener noreferrer"
          :class="[
            'group rounded-2xl border bg-white p-7 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-card-hover)]',
            product.family === 'INQ'
              ? 'border-violet-200 border-t-4 border-t-orange-400'
              : 'border-emerald-200 border-t-4 border-t-amber-400',
          ]"
        >
          <div
            :class="[
              'mb-5 flex size-11 items-center justify-center rounded-lg',
              product.family === 'INQ' ? 'bg-violet-50 text-violet-800' : 'bg-emerald-50 text-emerald-800',
            ]"
          >
            <UIcon
              :name="product.icon"
              class="size-6"
            />
          </div>
          <span
            :class="[
              'w-fit rounded-full px-2.5 py-1 text-xs font-semibold',
              product.family === 'INQ'
                ? 'bg-violet-100 text-violet-900 ring-1 ring-orange-300'
                : 'bg-emerald-100 text-emerald-900 ring-1 ring-amber-300',
            ]"
          >
            {{ product.family }} product site
          </span>
          <h3 class="text-xl font-heading font-bold text-gray-900 group-hover:text-brand-700">
            {{ product.name }}
          </h3>
          <p class="mt-2 text-gray-600">
            {{ product.description }}
          </p>
          <span class="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-700">
            Visit {{ product.family }} site
            <UIcon
              name="i-heroicons-arrow-up-right-20-solid"
              class="size-4"
            />
          </span>
        </a>
      </div>
    </section>

    <section
      id="services"
      class="max-w-6xl mx-auto scroll-mt-24 px-4 pb-20"
    >
      <div class="text-center mb-10">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-brand-600 mb-2">
          What We Do
        </h2>
        <p class="text-2xl sm:text-3xl font-heading font-bold text-gray-900">
          A specialist set of compliance services
        </p>
      </div>
      <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <ServiceCard
          v-for="service in services"
          :key="service.to"
          :icon="service.icon"
          :title="service.title"
          :description="service.description"
          :to="service.to"
        />
      </div>
    </section>

    <section
      id="industries"
      class="scroll-mt-24 bg-gray-50 py-20"
    >
      <div class="max-w-6xl mx-auto px-4 text-center">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-brand-600 mb-2">
          Industries
        </h2>
        <p class="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-4">
          Context matters to good quality practice
        </p>
        <p class="text-gray-600 mb-8 max-w-2xl mx-auto">
          The QikSolve team have deep expertise supporting quality-critical organisations of every size.
        </p>
        <NuxtLink
          to="/industries"
          class="mb-12 inline-flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          View all industries
          <UIcon
            name="i-heroicons-arrow-right-20-solid"
            class="size-4"
            aria-hidden="true"
          />
        </NuxtLink>

        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 text-left">
          <div
            v-for="industry in industries"
            :key="industry.title"
            class="rounded-2xl bg-white border border-gray-200 p-6 shadow-[var(--shadow-card)]"
          >
            <div class="mb-4 flex size-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <UIcon
                :name="industry.icon"
                class="size-5"
              />
            </div>
            <h3 class="font-semibold text-gray-900 mb-2">
              {{ industry.title }}
            </h3>
            <p class="text-sm text-gray-600">
              {{ industry.description }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      id="insights"
      class="max-w-6xl mx-auto scroll-mt-24 px-4 py-20"
    >
      <div class="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div class="max-w-2xl">
          <h2 class="text-sm font-semibold uppercase tracking-wider text-brand-600 mb-2">
            Insights
          </h2>
          <p class="text-3xl font-heading font-bold text-gray-900 mb-3">
            Build confidence before you choose a path.
          </p>
          <p class="text-gray-600">
            Explore practical guidance on GMP, ISO 9001, SharePoint, quality systems, and the decisions that shape
            regulated operations.
          </p>
        </div>
        <UButton
          to="/blog"
          variant="outline"
          trailing-icon="i-heroicons-arrow-right-20-solid"
          class="text-brand-700 ring-brand-700"
        >
          Browse Insights
        </UButton>
      </div>
    </section>

    <section class="max-w-4xl mx-auto px-4 py-20 text-center">
      <h2 class="text-2xl sm:text-3xl font-heading font-bold mb-4 text-gray-900">
        About QikSolve
      </h2>
      <p class="text-gray-600 mb-4">
        Founded in 2014, QikSolve are a group of passionate consultants and developers based in and around
        Melbourne.
      </p>
      <p class="text-gray-600 mb-8">
        We want to provide solutions that are used and inspire the statement 'I bet we could use this to do
        that!' opening the eyes of our customers to a new way of working. Most companies already have free or
        low cost access to SharePoint and its functionality is ideal for the forms, workflows and collaboration
        needed for a great Quality Management System or QMS. We aim to replace your repetitive and
        non-value-adding, out-dated, paper-based quality management systems with simple, automated workflows,
        which can be accessed from any device, anywhere.
      </p>
      <UButton
        to="/about"
        size="lg"
        variant="subtle"
        trailing-icon="i-heroicons-arrow-right-20-solid"
        class="text-brand-700 ring-brand-700"
      >
        Learn more about us
      </UButton>
    </section>

    <section class="bg-brand-900 text-white">
      <div class="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 class="text-2xl sm:text-3xl font-heading font-bold mb-3">
          Ready to make more of Microsoft 365 for quality?
        </h2>
        <p class="text-brand-100 mb-8">
          Bring your quality challenge to QikSolve. We can help you understand the options, from strengthening
          existing Microsoft 365 workflows to choosing the right product and implementation pathway.
        </p>
        <UButton
          to="/contact"
          size="xl"
          trailing-icon="i-heroicons-arrow-right-20-solid"
          class="bg-white text-brand-900 hover:bg-brand-50 focus-visible:outline-white"
        >
          Book a discovery call
        </UButton>
      </div>
    </section>
  </div>
</template>
